import { AlertCircle, AlertTriangle, WifiOff } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/common/utils/formatDateTime';
import { resolveEmsLevel, type EmsLevel } from '@/features/telemetry/utils/resolveEmsLevel';

export interface EmsStatusBarProps {
  lastMessageAt: Date | null;
  isOnline: boolean;
  now: Date;
}

const LEVEL_STYLES: Record<EmsLevel, { dot: string; pill: string; text: string }> = {
  ok: { dot: 'bg-emerald-500', pill: 'border-emerald-200 bg-emerald-50', text: 'text-emerald-700' },
  warning: { dot: 'bg-amber-400', pill: 'border-amber-200 bg-amber-50', text: 'text-amber-700' },
  error: { dot: 'bg-red-500', pill: 'border-red-200 bg-red-50', text: 'text-red-700' },
  offline: {
    dot: 'bg-muted-foreground/40',
    pill: 'border-border bg-muted',
    text: 'text-muted-foreground',
  },
};

function levelLabel(level: EmsLevel): string {
  switch (level) {
    case 'ok':
      return t`Online`;
    case 'warning':
      return t`Connection delayed`;
    case 'error':
      return t`Connection error`;
    case 'offline':
      return t`Offline`;
  }
}

function lastMessageLabel(ageMs: number): string {
  const ageSeconds = Math.floor(ageMs / 1000);
  if (ageSeconds < 60) {
    return t`Last message: ${ageSeconds} s ago`;
  }
  const ageMinutes = Math.floor(ageSeconds / 60);
  return t`Last message: ${ageMinutes} min ago`;
}

/**
 * EMS connectivity status pill: a coloured dot, a level label, and how long ago the
 * last telemetry message arrived. Backed by a mock service today — see
 * services/EmsStatusService.ts for the proposed real endpoint.
 */
export function EmsStatusBar({ lastMessageAt, isOnline, now }: EmsStatusBarProps) {
  const level = resolveEmsLevel(lastMessageAt, isOnline, now);
  const styles = LEVEL_STYLES[level];
  const Icon =
    level === 'warning'
      ? AlertTriangle
      : level === 'error'
        ? AlertCircle
        : level === 'offline'
          ? WifiOff
          : null;

  const ageMs = lastMessageAt ? now.getTime() - lastMessageAt.getTime() : null;

  return (
    <div
      data-testid="ems-status-bar"
      className={cn('flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm', styles.pill)}
    >
      <span className={cn('size-2 shrink-0 rounded-full', styles.dot)} aria-hidden="true" />
      <div className="flex items-center gap-1.5">
        {Icon && (
          <Icon size={13} className={styles.text} data-testid={`ems-status-icon-${level}`} />
        )}
        <span className={cn('font-semibold', styles.text)}>{levelLabel(level)}</span>
      </div>
      {ageMs !== null && (
        <span className={cn('opacity-70', styles.text)}>{lastMessageLabel(ageMs)}</span>
      )}
      {lastMessageAt && (
        <span className="ml-auto hidden text-xs tabular-nums text-muted-foreground sm:block">
          {formatDateTime(lastMessageAt, 'dateTimeWithSec')}
        </span>
      )}
    </div>
  );
}
